import { patchUser } from "@/api/privateRequests/user/patchUser";
import Button from "@/components/Button";
import {
  buildFeaturebaseRedirectUrl,
  isValidReturnUrl,
  normalizeReturnUrl,
  validateFeedbackPortalDisplayName,
} from "@/pages/FeaturebaseSso/featureBaseFunctions";
import { showModal } from "@/redux/message/messageSlice";
import { AppDispatch } from "@/redux/store";
import { getUserSelector, setUpdateUser } from "@/redux/user/userSlice";
import { useTranslation } from "@repo/shared/i18n";
import { captureMessage } from "@sentry/react";
import { MouseEvent, useEffect, useMemo, useRef, useState } from "react";
import { Oval } from "react-loading-icons";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router";

export const FeaturebaseSso = () => {
  const { t } = useTranslation(["auth", "common"]);
  const location = useLocation();
  const { user, status } = useSelector(getUserSelector);
  const dispatch = useDispatch<AppDispatch>();
  const [error, setError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const loginPrompted = useRef(false);
  const [needsLogin, setNeedsLogin] = useState(false);

  const redirectTo = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const redirectToParam = searchParams.get("return_to");
    if (redirectToParam) {
      const normalizedUrl = normalizeReturnUrl(redirectToParam);
      if (isValidReturnUrl(normalizedUrl)) {
        return normalizedUrl;
      }
    }
    return null;
  }, [location.search]);

  // Check if we're still loading the user state
  const hasAccessToken = !!localStorage.getItem("access_token");
  const isLoadingUser =
    hasAccessToken && (status === "idle" || status === "pending");

  useEffect(() => {
    if (user?.feedbackPortalDisplayName) {
      setDisplayName(user.feedbackPortalDisplayName);
    }
  }, [user?.feedbackPortalDisplayName]);

  const redirectToFeaturebase = async () => {
    try {
      setIsRedirecting(true);
      if (!redirectTo) {
        throw new Error("Missing return URL");
      }
      const featurebaseUrl = await buildFeaturebaseRedirectUrl(redirectTo);
      window.location.href = featurebaseUrl;
    } catch (err) {
      console.error("Failed to get Featurebase JWT:", err);
      setError(t("featurebase.authFailed"));
      setIsRedirecting(false);
    }
  };

  useEffect(() => {
    // Wait for user fetch to complete before making any decisions
    // Also prevent retry if an error has already been set
    if (isLoadingUser || isSubmitting || isRedirecting || error) {
      return;
    }

    const handleSso = async () => {
      // Validate return_to parameter
      if (!redirectTo) {
        setError(t("featurebase.invalidReturnUrl"));
        captureMessage("FeatureBase SSO: Invalid or missing return URL.", {
          level: "error",
          tags: { error_type: "invalid_return_url" },
          extra: { redirectTo: redirectTo },
        });
        return;
      }

      // User fetch has completed - check if logged in
      if (!user?.uuid) {
        if (!loginPrompted.current) {
          loginPrompted.current = true;
          dispatch(showModal({ modalType: "login" }));
        }
        setNeedsLogin(true);
        return;
      }

      // User is logged in, if they already have a display name proceed to SSO
      if (user.feedbackPortalDisplayName) {
        redirectToFeaturebase();
      }
    };

    handleSso();
  }, [
    user?.uuid,
    user?.feedbackPortalDisplayName,
    dispatch,
    redirectTo,
    isLoadingUser,
    isSubmitting,
    isRedirecting,
    error,
  ]);

  const handleSubmit = async (event: MouseEvent) => {
    event.preventDefault();
    if (!redirectTo) {
      setError(t("featurebase.invalidReturnUrl"));
      return;
    }

    const {
      isValid,
      error: validationError,
      trimmed,
    } = validateFeedbackPortalDisplayName(displayName);

    if (!isValid) {
      setFormError(validationError);
      return;
    }

    setFormError(null);
    setIsSubmitting(true);

    try {
      await patchUser({ feedbackPortalDisplayName: trimmed });
      dispatch(setUpdateUser({ feedbackPortalDisplayName: trimmed }));
      await redirectToFeaturebase();
    } catch (err) {
      console.error("Failed to set Featurebase display name:", err);
      setFormError(t("featurebase.saveFailed"));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (error) {
    return (
      <div className="flex flex-col items-center w-full mt-16 text-white gap-y-8 px-4">
        <h1 className="text-lg text-error">{error}</h1>
        <Button
          variant="primary"
          label={t("featurebase.returnButton")}
          onClick={() => {
            window.location.href = "https://feedback.easysbc.io";
          }}
        />
      </div>
    );
  }

  if (
    !isLoadingUser &&
    user?.uuid &&
    !user.feedbackPortalDisplayName &&
    redirectTo
  ) {
    return (
      <div className="flex flex-col items-center w-full mt-16 text-white gap-y-6 px-4">
        <div className="flex flex-col items-center gap-y-2 text-center max-w-xl">
          <h1 className="text-xl font-semibold">
            {t("featurebase.setDisplayNameTitle")}
          </h1>
          <p className="text-gray-300">
            {t("featurebase.setDisplayNameDescription")}
          </p>
        </div>
        <div className="w-full max-w-md flex flex-col gap-y-3">
          <label className="flex flex-col gap-y-2 text-left">
            <span className="text-sm text-gray-200">
              {t("featurebase.displayName")}
            </span>
            <input
              className="w-full rounded-md border border-gray-600 bg-[#1A242D] px-3 py-2 text-white outline-none focus:border-primary-blue"
              value={displayName}
              onChange={(e) => {
                setDisplayName(e.target.value);
                if (formError) {
                  setFormError(null);
                }
              }}
              placeholder={t("featurebase.displayNamePlaceholder")}
              maxLength={30}
            />
          </label>
          {formError && (
            <p className="text-sm text-error" role="alert">
              {formError}
            </p>
          )}
          <Button
            variant="primary"
            label={
              isSubmitting ? t("featurebase.saving") : t("common:continue")
            }
            disabled={isSubmitting}
            onClick={handleSubmit}
          />
        </div>
      </div>
    );
  }

  if (needsLogin && !user?.uuid) {
    return (
      <div className="flex flex-col items-center w-full mt-16 text-white gap-y-6 px-4 text-center">
        <h1 className="text-xl font-semibold">
          {t("featurebase.loginRequired")}
        </h1>
        <Button
          variant="primary"
          label={t("common:logIn")}
          onClick={() => dispatch(showModal({ modalType: "login" }))}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center w-full mt-16 text-white gap-y-4">
      <Oval width="60" height="60" stroke="#0396FF" />
      <p className="text-gray-300">{t("featurebase.loggingIn")}</p>
    </div>
  );
};
